/**
 * THE TURN INTENT - what the turn card says (v8.321.0).
 *
 * The turn card hangs under the row of the combatant you are acting as
 * and reads the attack as it stands: the armed weapon and its fire mode,
 * the modifiers in play, the target with the map's range and cover, the
 * odds, and the trigger's label. It is a PROJECTION over state the shell
 * already holds - the rack, the ledger, the live map line, the forecast,
 * the chamber - and nothing here rolls, writes or decides anything.
 *
 * The one thing it adds is the VERDICT: the single line that overrides
 * the rest when there is no shot to speak of, in the map readout's own
 * voice - NO WEAPON ARMED, NO TARGET, TARGET DOWN, LINE OF SIGHT BLOCKED,
 * BEYOND RANGE. Ranked in that order: a downed target is the fact that
 * matters most, and a blocked line beats a long one.
 *
 * Pure, zero React dependency.
 */
import { RANGE_BAND_LABELS } from './attackCalc';
import type { Cover } from '../types';

interface IntentChip {
  id: string;
  label: string;
  value: number;
  /** The ledger's provenance: stance, quality, custom, condition, range. */
  source: string;
}

export interface TurnIntentInput {
  actorName: string | null;
  weapon: { name: string; isMelee: boolean } | null;
  /** The fire selector's current detent label, or null with no weapon. */
  fireModeLabel: string | null;
  /** The modifier ledger's chips, as they are. */
  chips: IntentChip[];
  target: { name: string; down: boolean } | null;
  /** The live actor→target line, or null when the map has no answer. */
  measurement: { metres: number; rangeBand: string | null; cover: Cover | null; blocked: boolean } | null;
  /** The forecast, or null while it is still being computed. */
  odds: { hitChance: number; meanDamage: number } | null;
  /** What the big button says: ATTACK, FEINT, CALLED SHOT - HEAD. */
  triggerLabel: string;
  canFire: boolean;
}

type IntentVerdict = 'NO WEAPON ARMED' | 'NO TARGET' | 'TARGET DOWN' | 'LINE OF SIGHT BLOCKED' | 'BEYOND RANGE';

export interface TurnIntent {
  actorName: string | null;
  weapon: string | null;
  mode: string | null;
  /** Every ledger chip but the range one - the range has its own line. */
  chips: IntentChip[];
  targetName: string | null;
  range: { metres: string; band: string } | null;
  /** "LOW COVER", or null for none or no line. */
  cover: string | null;
  verdict: IntentVerdict | null;
  odds: { hit: string; dmg: string } | null;
  trigger: string;
  canFire: boolean;
}

const COVER_LABELS: Record<Cover['level'], string | null> = {
  none: null,
  low: 'LOW COVER',
  half: 'HALF COVER',
  threeQuarter: '3/4 COVER',
  full: 'FULL COVER',
};

function rangeLine(input: TurnIntentInput): TurnIntent['range'] {
  const m = input.measurement;
  if (!m) return null;
  const metres = `${Math.round(m.metres)} m`;
  if (input.weapon?.isMelee) return { metres, band: 'MELEE' };
  if (!m.rangeBand) return { metres, band: 'BEYOND RANGE' };
  // "Short Range" is the ledger's word; the card's line already says range.
  return { metres, band: (RANGE_BAND_LABELS[m.rangeBand] ?? m.rangeBand).replace(/ Range$/, '').toUpperCase() };
}

function verdictOf(input: TurnIntentInput): IntentVerdict | null {
  if (!input.weapon) return 'NO WEAPON ARMED';
  if (!input.target) return 'NO TARGET';
  if (input.target.down) return 'TARGET DOWN';
  const m = input.measurement;
  if (m?.blocked) return 'LINE OF SIGHT BLOCKED';
  if (m && !input.weapon.isMelee && !m.rangeBand) return 'BEYOND RANGE';
  return null;
}

export function turnIntent(input: TurnIntentInput): TurnIntent {
  const verdict = verdictOf(input);
  const cover = input.measurement?.cover ? COVER_LABELS[input.measurement.cover.level] : null;
  return {
    actorName: input.actorName,
    weapon: input.weapon?.name ?? null,
    mode: input.weapon ? input.fireModeLabel : null,
    chips: input.chips.filter((c) => c.source !== 'range'),
    targetName: input.target?.name ?? null,
    range: rangeLine(input),
    cover,
    verdict,
    odds: input.odds
      ? { hit: `${Math.round(input.odds.hitChance * 100)} %`, dmg: input.odds.meanDamage.toFixed(1) }
      : null,
    trigger: input.triggerLabel,
    // A verdict about the weapon is the one that takes the trigger away;
    // the others inform, and the table may still fire (the sim never
    // argues with the GM).
    canFire: input.canFire && !!input.weapon,
  };
}
